/**
 * Atribución de partner / promo code (programa influencers NER-3079, Phase 3).
 *
 * Flujo:
 *  1. La landing v2 `nerdo.solutions/resto/con/{handle}` setea la cookie
 *     `nerdo_partner={code}` (Domain=.nerdo.solutions, 30 días) y ademas propaga
 *     el code como query param `?promoCode=...&attributionSource=cookie` al link
 *     de signup (admin.nerdo.solutions/resto/signup).
 *  2. Al montar la app llamamos `capturePromoAttribution()`: guarda la atribución
 *     en localStorage para que sobreviva el wizard de registro multi-paso.
 *  3. `createSubscription` (services/api.js) mergea la atribución guardada en el
 *     body del `POST /subscriptions` (a menos que el caller ya haya seteado un code).
 *
 * `attributionSource`: 'code' = el usuario lo tipeó/editó manualmente; 'cookie' =
 * vino del link/cookie sin editar. El backend normaliza uppercase y sólo atribuye
 * si el code está `active` (first-wins inmutable) — un code inválido no bloquea el alta.
 */

import { storage } from './storage';

const STORAGE_KEY = 'promoAttribution';
const COOKIE_NAME = 'nerdo_partner';
const CODE_RE = /^[A-Z0-9]{4,20}$/;

function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const row = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : null;
}

function normalize(code) {
  if (!code) return null;
  const c = String(code).trim().toUpperCase();
  return CODE_RE.test(c) ? c : null;
}

export function getStoredPromoAttribution() {
  try {
    const raw = storage.get(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const code = normalize(parsed.code);
    if (!code) return null;
    return { code, source: parsed.source === 'code' ? 'code' : 'cookie' };
  } catch {
    return null;
  }
}

export function setStoredPromoAttribution(code, source) {
  const normalized = normalize(code);
  if (!normalized) {
    storage.remove(STORAGE_KEY);
    return null;
  }
  const value = { code: normalized, source: source === 'code' ? 'code' : 'cookie' };
  storage.set(STORAGE_KEY, JSON.stringify(value));
  return value;
}

export function clearPromoAttribution() {
  storage.remove(STORAGE_KEY);
}

/**
 * Captura la atribución al montar la app. Prioridad: query param > cookie.
 * Idempotente: el query param siempre refresca; la cookie sólo aplica si no hay
 * nada guardado (no pisa una entrada manual previa). Devuelve la atribución vigente.
 */
export function capturePromoAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('promoCode');
    if (normalize(urlCode)) {
      const src = params.get('attributionSource') === 'code' ? 'code' : 'cookie';
      return setStoredPromoAttribution(urlCode, src);
    }
    const cookieCode = readCookie(COOKIE_NAME);
    if (normalize(cookieCode) && !getStoredPromoAttribution()) {
      return setStoredPromoAttribution(cookieCode, 'cookie');
    }
  } catch (err) {
    console.warn('[promoAttribution] capture failed:', err);
  }
  return getStoredPromoAttribution();
}

/**
 * Mergea la atribución guardada en un payload de createSubscription.
 * No pisa un `promoCode` que el caller ya haya seteado explícitamente.
 */
export function mergePromoAttribution(data) {
  if (data && data.promoCode) return data;
  const stored = getStoredPromoAttribution();
  if (!stored) return data;
  return { ...data, promoCode: stored.code, attributionSource: stored.source };
}
